import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { PausePreviewDto, PauseResultDto } from '@tiffinify/contracts';
import { addMonths, formatDay, formatMonth, mealsOf, type CalendarCell, type Slot } from '@tiffinify/core';
import { TiffinMini, PageLoader, Card, CalHeader, ErrorText, Icon, Legend, MealPicker, MonthGrid, Skel, ToggleRow, LoadError } from '../components/ui';
import { Back, Btn, Card as PCard, Empty as PEmpty, Hero, Kick, Screen, Title, Top } from '../components/p';
import type { Flash } from '../lib/flash';
import { post, put, isNotFound } from '../lib/api';
import { errorMessage } from '../lib/format';
import { useMe, useProfile } from '../lib/queries';

export function Pause() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const today = useMe().data?.today ?? '';
  const [month, setMonth] = useState<string | undefined>(undefined);
  // The meals to change; null = every meal on the plan.
  const [picked, setPicked] = useState<Slot | null>(null);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState(true);
  const profile = useProfile(id, month);
  const p = profile.data;
  const sub = p?.subscription;
  const choice: Slot | undefined = picked ?? sub?.slot;

  // Which of the touched days are currently paused (for the chosen meals)? Those become "resume".
  const pausedNow = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const x of p?.pauses ?? []) m.set(x.day, new Set<string>(mealsOf(x.slot)));
    return m;
  }, [p?.pauses]);

  const planMeals = sub ? mealsOf(sub.slot) : [];
  const wanted = choice ? mealsOf(choice) : planMeals;
  const isPaused = (day: string) => {
    const s = pausedNow.get(day);
    return !!s && wanted.every((m) => s.has(m));
  };

  const pause = [...touched].filter((d) => !isPaused(d)).sort();
  const resume = [...touched].filter((d) => isPaused(d)).sort();
  const body = { slot: choice, pause, resume };

  const preview = useQuery({
    queryKey: ['pause-preview', id, choice, pause, resume],
    queryFn: () => post<PausePreviewDto>(`/subscriptions/${sub!.id}/pauses/preview`, body),
    enabled: !!sub && !!choice && touched.size > 0,
    placeholderData: (prev) => prev,
  });

  const save = useMutation({
    mutationFn: () => put<PauseResultDto>(`/subscriptions/${sub!.id}/pauses`, body),
    onSuccess: (res) => {
      void qc.invalidateQueries();
      const n = res.effectiveDays.length;
      const flash: Flash = {
        text: `Saved. ${n} ${n === 1 ? 'day' : 'days'} changed; the plan now ends ${formatDay(res.newEndDate)}.`,
        ...(confirm ? { wa: { label: `Send to ${p!.subscriber.name.split(' ')[0]}`, link: res.message.link } } : {}),
      };
      nav(`/s/${id}`, { state: { flash } });
    },
  });

  const first = p?.subscriber.name.split(' ')[0] ?? '';
  const head = <Top><Back to={`/s/${id}`} label={first || 'Back'} /></Top>;
  if (profile.isPending) return <Screen gap={18}>{head}<Title h="Pause days" sub={<Skel w={160} />} /><PageLoader label="Checking pauses" /></Screen>;
  if (profile.isError && !isNotFound(profile.error)) return <Screen gap={18}>{head}<LoadError what="this plan" onRetry={() => void profile.refetch()} /></Screen>;
  if (!p || !sub || !p.summary || !p.calendar) return <Screen gap={18}>{head}<PCard><PEmpty title="No active plan" /></PCard></Screen>;

  const shown = p.calendar.month;
  const pv = preview.data;
  const oldEnd = p.summary.endDate;
  const newEnd = touched.size > 0 && pv ? pv.newEndDate : oldEnd;
  const moved = newEnd !== oldEnd;

  const selectable = (c: CalendarCell) => c.date >= today && ['today', 'upcoming', 'paused', 'partial', 'end'].includes(c.state);
  const classFor = (c: CalendarCell): string => {
    if (c.date < today) return c.state === 'none' ? '' : 'off';
    if (touched.has(c.date)) return isPaused(c.date) ? 'meal' : 'sel';
    if (moved && c.date === newEnd) return 'added';
    if (moved && c.date === oldEnd) return 'wasend';
    if (c.state === 'none') return '';
    if (c.state === 'paused' || c.state === 'partial') return c.state === 'paused' || isPaused(c.date) ? 'paused' : 'partial';
    if (c.state === 'off' || c.state === 'holiday') return c.state;
    if (c.state === 'end') return 'end';
    return c.state === 'today' ? 'today' : 'meal';
  };

  const toggleDay = (d: string) => setTouched((s) => { const n = new Set(s); n.has(d) ? n.delete(d) : n.add(d); return n; });
  // Until the preview answers, count the days picked, so the button never reads "Pause 0 days".
  const effective = pv ? pv.effectiveDays.length : touched.size;
  const label = pause.length && resume.length ? `Update ${effective} days` : resume.length ? `Resume ${effective} ${effective === 1 ? 'day' : 'days'}` : `Pause ${effective} ${effective === 1 ? 'day' : 'days'}`;

  return (
    <Screen gap={18}>
      {head}
      <Title h="Pause days" sub={`Tap the days ${first} will skip. They move to the end.`} />

      <PCard pad>
        <CalHeader title={formatMonth(shown)} onPrev={() => setMonth(addMonths(shown, -1))} onNext={() => setMonth(addMonths(shown, 1))} />
        <MonthGrid cells={p.calendar.cells} aria={`Pick days to pause in ${formatMonth(shown)}`} onPick={toggleDay} classFor={classFor} disabledFor={(c) => !selectable(c)} />
        <Legend items={[{ cls: 'sel', label: 'Changing' }, { cls: 'paused', label: 'Paused now' }, { cls: 'added', label: 'New end' }, { cls: 'end', label: 'Ends' }]} />
        <p className="helper"><Icon name="info" size={16} color="var(--ink-2)" /><span>Tap a day to pause it. Tap a paused day to bring it back. Days off the plan and holidays are greyed out.</span></p>
      </PCard>

      {planMeals.length > 1 && choice && (
        <MealPicker label="Meals to change" value={choice} allowed={planMeals} onChange={(v) => { setPicked(v); setTouched(new Set()); }} />
      )}

      <Hero label="New end date">
        <Kick s>{moved ? 'New end date' : 'End date'}</Kick>
        <div className="p-endmove">
          {moved && <s>{formatDay(oldEnd)}</s>}
          {moved && <Icon name="arrow" size={20} sw={2.4} color="var(--p-saffron)" />}
          <b>{formatDay(newEnd)}</b>
          {preview.isFetching && <span className="hero-busy" aria-label="Recalculating"><TiffinMini /></span>}
        </div>
        <span className="p-muted">
          {touched.size === 0 ? 'Pick the days to skip. The new end date shows here before you save.' : (
            <><b>{effective} {effective === 1 ? 'day' : 'days'} change.</b> No meals are lost.{pv && pv.skippedDays.length > 0 ? ` ${pv.skippedDays.length} picked ${pv.skippedDays.length === 1 ? 'day is' : 'days are'} already off.` : ''}</>
          )}
        </span>
      </Hero>

      <div className="p-links"><span className="p-muted">Want the meal on another day instead?</span><Link to={`/s/${id}/move`}>Move it</Link></div>
      <Card className="toggle-card"><ToggleRow title={`Tell ${first} on WhatsApp`} sub="With the new end date" on={confirm} onChange={setConfirm} /></Card>
      <ErrorText>{save.error ? errorMessage(save.error) : preview.error ? errorMessage(preview.error) : ''}</ErrorText>
      <Btn busy={save.isPending} label={touched.size ? `${label}${confirm ? ` · tell ${first}` : ''}` : 'Pick days'} go="check" onClick={() => save.mutate()} disabled={save.isPending || touched.size === 0 || effective === 0} />
    </Screen>
  );
}
